import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:bg-muted disabled:text-muted-foreground disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-brand-primary text-white hover:bg-brand-primary-dark",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-input bg-background text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-brand-secondary text-white hover:bg-brand-secondary-dark",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        info: "bg-info text-white hover:bg-info/90 focus-visible:ring-info/20 dark:focus-visible:ring-info/40",
        success: "bg-success text-white hover:bg-success/90 focus-visible:ring-success/20 dark:focus-visible:ring-success/40",
        warning: "bg-warning text-white hover:bg-warning/90 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40",
        error: "bg-error text-white hover:bg-error/90 focus-visible:ring-error/20 dark:focus-visible:ring-error/40",
        primaryLight:
          "bg-brand-primary-light text-white hover:bg-brand-primary-light/90 focus-visible:ring-brand-primary-light/20 dark:text-black dark:focus-visible:ring-brand-primary-light/40",
        primaryDark:
          "bg-brand-primary-dark text-white hover:bg-brand-primary-dark/90 focus-visible:ring-brand-primary-dark/20 dark:focus-visible:ring-brand-primary-dark/40",
        secondaryLight:
          "bg-brand-secondary-light text-white hover:bg-brand-secondary-light/90 focus-visible:ring-brand-secondary-light/20 dark:focus-visible:ring-brand-secondary-light/40",
        secondaryDark:
          "bg-brand-secondary-dark text-white hover:bg-brand-secondary-dark/90 focus-visible:ring-brand-secondary-dark/20 dark:focus-visible:ring-brand-secondary-dark/40",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  disabled,
  disabledTooltip,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Shown via Radix tooltip when the button is disabled (native title does not work with pointer-events-none). */
    disabledTooltip?: string
  }) {
  const Comp = asChild ? Slot : "button"

  const button = (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }), 'cursor-pointer')}
      disabled={disabled}
      {...props}
    />
  )

  if (disabled && disabledTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return button
}

export { Button, buttonVariants }
